import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { PRICING_PLANS, ROLES, FEATURE_ACCESS } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

// Helper function to create status chips
const createStatusChip = (status, configs) => {
  const config = configs[status] || configs.default || { label: status || 'Unknown', color: 'default' };
  return {
    label: config.label || status || 'Unknown',
    color: config.color || 'default',
    variant: config.variant || 'filled',
    size: 'small'
  };
};

// Status configurations
const statusConfigs = {
  active: { label: 'Active', color: 'success' },
  inactive: { label: 'Inactive', color: 'error' },
  default: { label: 'Unknown', color: 'default' }
};

export default function PricingPlanManagementPage() {
  const [plans, setPlans] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ name: '', role_id: '', price: '', tier: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ 
    name: '', 
    role_id: '', 
    price: '', 
    test_limit: '', 
    student_limit: '', 
    child_limit: '', 
    code: '', 
    tier: '',
    stripe_price_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [plansWithFeatures, setPlansWithFeatures] = useState(new Set());

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data; // non-paginated
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const allPlans = await fetchPaginated(PRICING_PLANS);
      setPlans(allPlans);
    } catch (err) {
      console.error('Error fetching plans:', err);
      showSnackbar('Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get(ROLES);
      setRoles(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [allPlans, allRoles, allFeatureAccess] = await Promise.all([
        fetchPaginated(PRICING_PLANS),
        fetchPaginated(ROLES),
        fetchPaginated(FEATURE_ACCESS),
      ]);
      setPlans(allPlans);
      setRoles(allRoles);
      const usedIds = new Set(
        (allFeatureAccess || []).map((fa) => fa.pricing_plan).filter((id) => id != null)
      );
      setPlansWithFeatures(usedIds);
    } catch (err) {
      console.error('Error fetching data:', err);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Debug: Monitor form changes
  useEffect(() => {
    console.log('Form state changed:', form);
  }, [form]);

  const handleOpen = (plan = null) => {
    if (plan) {
      const formData = { 
        name: plan.name, 
        role_ids: [plan.role_id], 
        price: String(plan.price), 
        test_limit: String(plan.test_limit), 
        student_limit: String(plan.student_limit || ''), 
        child_limit: String(plan.child_limit || ''), 
        code: plan.code || '', 
        tier: plan.tier || '',
        stripe_price_id: plan.stripe_price_id || ''
      };
      setForm(formData);
      setEditMode(true);
      setCurrentId(plan.id);
    } else {
      setForm({ 
        name: '', 
        role_ids: [], 
        price: '', 
        test_limit: '', 
        student_limit: '', 
        child_limit: '', 
        code: '', 
        tier: '',
        stripe_price_id: ''
      });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ 
      name: '', 
      role_ids: [], 
      price: '', 
      test_limit: '', 
      student_limit: '', 
      child_limit: '', 
      code: '', 
      tier: '',
      stripe_price_id: ''
    });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price || !Array.isArray(form.role_ids) || form.role_ids.length === 0) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    
    const selectedRoles = roles.filter((r) => form.role_ids.includes(r.id));
    if (selectedRoles.length === 0) {
      showSnackbar('Please select at least one role', 'error');
      return;
    }

    // Code is always required
    if (!form.code || !form.code.trim()) {
      showSnackbar('Code is required for the pricing plan', 'error');
      return;
    }

    // Role-specific limits
    const roleNames = selectedRoles.map((r) => r.role_name);

    if (roleNames.includes('PARENT')) {
      if (form.child_limit === '' || form.child_limit === null) {
        showSnackbar('Child limit is required for Parent pricing plans', 'error');
        return;
      }
    }

    if (roleNames.includes('TEACHER')) {
      if (form.student_limit === '' || form.student_limit === null) {
        showSnackbar('Student limit is required for Teacher pricing plans', 'error');
        return;
      }
    }

    const basePayload = { 
      ...form, 
      price: Number(form.price), 
      test_limit: Number(form.test_limit), 
      student_limit: form.student_limit && form.student_limit !== '' ? Number(form.student_limit) : null, 
      child_limit: form.child_limit && form.child_limit !== '' ? Number(form.child_limit) : null, 
      code: form.code, 
      tier: form.tier && form.tier !== '' ? Number(form.tier) : null,
      stripe_price_id: form.stripe_price_id && form.stripe_price_id.trim() !== '' ? form.stripe_price_id.trim() : null
    };
    
  

    try {
      if (editMode) {
        const roleId = form.role_ids && form.role_ids.length > 0 ? Number(form.role_ids[0]) : null;
        const payload = { ...basePayload, role_id: roleId };
        await api.patch(`${PRICING_PLANS}${currentId}/`, payload);
        showSnackbar('Pricing plan updated successfully');
      } else {
        // Create one plan per selected role
        for (const roleId of form.role_ids) {
          const payload = { ...basePayload, role_id: Number(roleId) };
          await api.post(PRICING_PLANS, payload);
        }
        showSnackbar('Pricing plan created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('[PricingPlan] Error', err?.response?.data || err.message);
      showSnackbar('Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing plan?')) return;
    try {
      await api.delete(`${PRICING_PLANS}${id}/`);
      showSnackbar('Pricing plan deleted successfully');
      fetchAll();
    } catch (err) {
      console.error('Error deleting pricing plan:', err);
      showSnackbar('Failed to delete pricing plan', 'error');
    }
  };

  const filteredPlans = filterData(plans, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { name: '', role_id: '', price: '', tier: '' });

  const filterConfig = [
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...'
    },
    {
      field: 'role_id',
      label: 'Role',
      type: 'select',
      options: roles.map(role => ({ value: role.id, label: role.role_name }))
    },
    {
      field: 'price',
      label: 'Price',
      type: 'text',
      placeholder: 'Search by price...'
    },
    {
      field: 'tier',
      label: 'Tier',
      type: 'text',
      placeholder: 'Search by tier...'
    }
  ];

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2
    },
    {
      field: 'role_name',
      headerName: 'Role',
      flex: 1,
      renderCell: (params) => {
        const roleName = params.value || 'Unknown';
        const chipProps = createStatusChip(roleName, {
          TEACHER: { label: 'Teacher', color: 'primary' },
          STUDENT: { label: 'Student', color: 'secondary' },
          PARENT: { label: 'Parent', color: 'success' },
          ADMIN: { label: 'Admin', color: 'default' },
          default: { label: roleName, color: 'default' }
        });
        return <Chip {...chipProps} />;
      }
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 1,
      valueGetter: (params) => `$${params.value}`
    },
    {
      field: 'test_limit',
      headerName: 'Test Limit',
      flex: 1
    },
    {
      field: 'student_limit',
      headerName: 'Student Limit',
      flex: 1
    },
    {
      field: 'tier',
      headerName: 'Tier',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'N/A'} 
          size="small" 
          color="default" 
          variant="outlined"
        />
      )
    },
    {
      field: 'stripe_price_id',
      headerName: 'Stripe ID',
      flex: 1.5,
      minWidth: 140,
      valueGetter: (params) => params.value || '—'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const used = plansWithFeatures.has(params.row.id);
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleOpen(params.row)}
                sx={{ p: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={used ? 'Cannot delete: plan used in feature access' : 'Delete'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => !used && handleDelete(params.row.id)}
                  sx={{ p: 0.5, color: used ? 'action.disabled' : 'error.main' }}
                  disabled={used}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  const actionButtons = [
    {
      label: 'Add Pricing Plan',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No pricing plans found',
    description: 'Try adjusting your filters or add your first pricing plan.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Pricing Plans"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredPlans.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <Box sx={{ flex: 1, width: '100%', overflowX: 'auto' }}>
          <DataGrid
            rows={filteredPlans}
            columns={columns}
            {...dataGridProps}
            autoHeight={false}
            sx={{ minWidth: 900 }}
            components={{
              Toolbar: () => (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                  p: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridToolbarColumnsButton 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarDensitySelector 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarExport 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                  </Box>
                </Box>
              )
            }}
          />
        </Box>
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Pricing Plan' : 'Add Pricing Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                margin="normal"
                required
                autoFocus
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Role</InputLabel>
                <Select
                  multiple
                  value={form.role_ids || []}
                  label="Role"
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || selected.length === 0) return 'Select roles';
                    const names = roles
                      .filter((r) => selected.includes(r.id))
                      .map((r) => r.role_name);
                    return names.join(', ');
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, role_ids: Array.isArray(value) ? value : [value] });
                  }}
                >
                  <MenuItem value="">
                    <em>Select roles</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                fullWidth
                margin="normal"
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Test Limit"
                type="number"
                value={form.test_limit}
                onChange={(e) => setForm({ ...form, test_limit: e.target.value })}
                fullWidth
                margin="normal"
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Student Limit"
                type="number"
                value={form.student_limit}
                onChange={(e) => setForm({ ...form, student_limit: e.target.value })}
                fullWidth
                margin="normal"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Child Limit"
                type="number"
                value={form.child_limit}
                onChange={(e) => setForm({ ...form, child_limit: e.target.value })}
                fullWidth
                margin="normal"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                fullWidth
                margin="normal"
                placeholder="e.g., TEACHER_PRO"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Tier"
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                fullWidth
                margin="normal"
                placeholder="e.g., Basic, Pro, Enterprise"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Stripe Price ID"
                value={form.stripe_price_id}
                onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
                fullWidth
                margin="normal"
                placeholder="e.g., price_1ABC..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
